import { useQuery } from '@tanstack/vue-query';
import { getCurrentInstance, onServerPrefetch } from 'vue';
import { queries } from '~/keys';
import { UAE_MODEL_CACHE_SECONDS, UAE_MODEL_DEFAULTS, type UaeModel } from '~/constants/uaeModel';
import { normalizeUaeModel, uaeModelCopyParams, type UaeModelInput } from '~/utils/uaeModel';

const fetchUaeModel = async (): Promise<UaeModel> => {
  try {
    const res = await $fetch<{ success?: boolean; data?: UaeModelInput }>('/api/public/uae-model');
    return normalizeUaeModel(res?.data);
  } catch {
    return { ...UAE_MODEL_DEFAULTS };
  }
};

/** UAE split/cap/cycle for /uae copy + calculator. SSR-prefetched (dehydrated into the payload); never throws. */
export const useUaeModel = () => {
  const query = useQuery({
    ...queries.uae.model,
    queryFn: fetchUaeModel,
    staleTime: UAE_MODEL_CACHE_SECONDS * 1000,
    retry: false,
  });

  if (import.meta.server && getCurrentInstance()) {
    onServerPrefetch(async () => {
      await query.suspense();
    });
  }

  const model = computed<UaeModel>(() => query.data.value ?? { ...UAE_MODEL_DEFAULTS });
  const copyParams = computed(() => uaeModelCopyParams(model.value));

  return {
    model,
    copyParams,
    isLoading: query.isLoading,
  };
};
